import type { PickedFile } from "@bundle/core";
import { useLayoutEffect, useRef } from "react";
import "./files.css";
import { pickedFiles, pickedTree, type PickedTreeFile } from "./media.tsx";

export type FilePickerProps = {
  /** Shows the system file picker (set from a press handler; it opens within that gesture). */
  open: boolean;
  onPick: (files: PickedFile[]) => void;
  /** The picker closed (picked or cancelled): reset `open`. */
  onClose: () => void;
  multiple?: boolean;
  /** "image/*,video/*" etc.; absent = any file. */
  accept?: string;
  /** Pick a whole folder (`webkitdirectory`): `onPickTree` gets every file inside with its folders. */
  directory?: boolean;
  onPickTree?: (files: PickedTreeFile[]) => void;
  testID: string;
};

/**
 * The system file picker, presented while `open`. Renders nothing visible. Native: SwiftUI `.fileImporter` (files) +
 * `PhotosPicker` (photos), Compose `rememberLauncherForActivityResult(PickMultipleVisualMedia / OpenMultipleDocuments)`.
 */
export function FilePicker({ open, onPick, onClose, multiple, accept, directory, onPickTree, testID }: FilePickerProps) {
  const input = useRef<HTMLInputElement>(null);
  const close = useRef(onClose);
  useLayoutEffect(() => {
    close.current = onClose;
  });

  // Layout effect: runs in the same task as the press that set `open`, so the browser still counts the user gesture.
  useLayoutEffect(() => {
    const el = input.current;
    if (!open || el === null) return;
    const cancel = () => close.current();
    el.addEventListener("cancel", cancel);
    el.value = "";
    el.click();
    return () => el.removeEventListener("cancel", cancel);
  }, [open]);

  return (
    <input
      ref={input}
      className="b-file-input"
      type="file"
      multiple={multiple}
      accept={accept}
      tabIndex={-1}
      aria-hidden
      data-testid={testID}
      {...(directory === true ? { webkitdirectory: "" } : {})}
      onChange={(e) => {
        if (directory === true && onPickTree !== undefined) {
          const tree = pickedTree(e.target.files);
          e.target.value = "";
          onClose();
          if (tree.length > 0) onPickTree(tree);
          return;
        }
        const files = pickedFiles(e.target.files);
        e.target.value = "";
        onClose();
        if (files.length > 0) onPick(files);
      }}
    />
  );
}
